import React, { useMemo } from 'react';
import { Card, CardContent, Typography, Box, Chip, IconButton, Stack } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Group } from '@visx/group';
import { scaleTime, scaleBand, scaleOrdinal } from '@visx/scale';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { useTooltip, useTooltipInPortal, defaultStyles } from '@visx/tooltip';
import { VisitRecord, DwellLocation } from '@signals/app-shell/types';

interface TemporalBreakdown {
  total_points?: number;
  daytime_points?: number;
  nighttime_points?: number;
}

interface DeviceResults {
  [deviceType: string]: {
    dwell_locations?: DwellLocation[];
  };
}

interface DeviceBreakdown {
  [deviceType: string]: DwellLocation[];
}

interface DwellGanttChartProps {
  dwellLocations: DwellLocation[];
  temporalBreakdown?: TemporalBreakdown;
  deviceBreakdown?: DeviceBreakdown;
  deviceResults?: DeviceResults;
  isMultiDevice?: boolean;
  selectedDeviceFilter?: string;
}

// Gantt chart data structures
interface GanttBar {
  locationName: string;
  startTime: Date;
  endTime: Date;
  duration: number;
  entityId: string;
  deviceType?: string;
  locationIndex: number;
}

interface GanttData {
  bars: GanttBar[];
  locations: string[];
  dateRange: [Date, Date];
  maxDuration: number;
  totalLocations?: number;
  currentPage?: number;
  locationsPerPage?: number;
}

// Chart dimensions and styling - Dynamic width for full page span
const CHART_HEIGHT = 500; // Increased height for better visibility
const MARGIN = { top: 40, right: 40, bottom: 80, left: 200 };

// Dynamic width calculation based on container
const useChartDimensions = () => {
  const [width, setWidth] = React.useState(1200); // Default width
  
  React.useEffect(() => {
    const updateWidth = () => {
      // Use viewport width minus margins and sidebar space
      const availableWidth = Math.min(window.innerWidth - 300, 1400); // Max 1400px, min space for UI
      setWidth(Math.max(800, availableWidth)); // Minimum 800px
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);
  
  return {
    chartWidth: width,
    chartHeight: CHART_HEIGHT,
    innerWidth: width - MARGIN.left - MARGIN.right,
    innerHeight: CHART_HEIGHT - MARGIN.top - MARGIN.bottom
  };
};

// Color palette for different entities/devices
const ENTITY_COLORS = [
  '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
  '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
];

const DEVICE_COLORS = {
  'Vehicle': '#1976d2',
  'Phone': '#388e3c', 
  'Watch': '#f57c00',
  'Unknown': '#757575'
};

// Tooltip styles
const tooltipStyles = {
  ...defaultStyles,
  minWidth: 60,
  backgroundColor: 'rgba(0,0,0,0.9)',
  color: 'white',
  fontSize: '12px',
  lineHeight: '18px',
  padding: '8px 12px',
};

const DwellGanttChart: React.FC<DwellGanttChartProps> = ({
  dwellLocations,
  temporalBreakdown: _temporalBreakdown,
  deviceBreakdown,
  deviceResults,
  isMultiDevice = false,
  selectedDeviceFilter = 'all',
}) => {
  const [currentPage, setCurrentPage] = React.useState(0);
  const locationsPerPage = 25;
  const { chartWidth, chartHeight, innerWidth, innerHeight } = useChartDimensions();
  
  // Reset to first page when data changes
  React.useEffect(() => {
    setCurrentPage(0);
  }, [dwellLocations, deviceResults, deviceBreakdown]);
  
  const ganttData: GanttData = useMemo(() => {
    const bars: GanttBar[] = [];
    const locationSet = new Set<string>();
    let minDate: Date | null = null;
    let maxDate: Date | null = null;
    let maxDuration = 0;

    // Extract dwell locations from the appropriate data structure
    let allDwellLocations: DwellLocation[] = [];
    
    if (isMultiDevice && deviceResults) {
      // Multi-device: extract from device_results
      (Object.entries(deviceResults) as [string, DeviceResults[string]][]).forEach(([deviceType, deviceData]) => {
        // Apply device filter for multi-device data
        if (selectedDeviceFilter !== 'all' && deviceType !== selectedDeviceFilter) {
          return; // Skip this device type if it doesn't match the filter
        }
        
        if (deviceData.dwell_locations) {
          deviceData.dwell_locations.forEach(location => {
            allDwellLocations.push({ ...location, deviceType } as any);
          });
        }
      });
    } else if (isMultiDevice && deviceBreakdown) {
      // Multi-device fallback: extract from device_breakdown
      (Object.entries(deviceBreakdown) as [string, DwellLocation[]][]).forEach(([deviceType, locations]) => {
        // Apply device filter for multi-device data
        if (selectedDeviceFilter !== 'all' && deviceType !== selectedDeviceFilter) {
          return; // Skip this device type if it doesn't match the filter
        }
        
        locations.forEach(location => {
          allDwellLocations.push({ ...location, deviceType } as any);
        });
      });
    } else {
      // Single-device: use direct dwell locations
      allDwellLocations = dwellLocations;
    }

    // Filter and sort all locations (by visits for consistency)
    const allActiveLocations = allDwellLocations
      .filter(dwell => dwell.total_visits && dwell.total_visits > 0)
      .sort((a, b) => (b.total_visits || 0) - (a.total_visits || 0));
    
    // Apply pagination
    const startIndex = currentPage * locationsPerPage;
    const activeLocations = allActiveLocations.slice(startIndex, startIndex + locationsPerPage);

    // Process each dwell location (paginated)
    activeLocations.forEach((location, locationIndex) => {
      const locationName = location.place_name || `Location ${locationIndex + 1}`;
      locationSet.add(locationName);
      
      const rawVisitTimes = location.visit_times || [];
      
      // Convert visit_times to VisitRecord format (handle both old and new formats)
      const visitRecords: VisitRecord[] = rawVisitTimes.map((item, index) => {
        if (typeof item === 'string') {
          // Old format: just timestamp strings
          return {
            start_time: item,
            end_time: null,
            duration_seconds: 30 * 60, // Default 30 minutes
            entity_id: `entity_${index}`
          };
        } else {
          // New format: already VisitRecord objects
          return item as VisitRecord;
        }
      });
      
      // Process visit records
      visitRecords.forEach((visit) => {
        try {
          const startTime = new Date(visit.start_time);
          let endTime: Date;
          
          // Calculate end time
          if (visit.end_time) {
            endTime = new Date(visit.end_time);
          } else if (visit.duration_seconds) {
            endTime = new Date(startTime.getTime() + (visit.duration_seconds * 1000));
          } else {
            // Default to 30 minutes if no end time or duration
            endTime = new Date(startTime.getTime() + (30 * 60 * 1000));
          }
          
          const duration = visit.duration_seconds || (endTime.getTime() - startTime.getTime()) / 1000;
          
          // Update date range
          if (!minDate || startTime < minDate) minDate = startTime;
          if (!maxDate || endTime > maxDate) maxDate = endTime;
          if (duration > maxDuration) maxDuration = duration;
          
          bars.push({
            locationName,
            startTime,
            endTime,
            duration,
            entityId: visit.entity_id,
            deviceType: (location as any).deviceType,
            locationIndex,
          });
        } catch (error) {
          console.warn('⚠️ Gantt: Error processing visit record:', visit, error);
        }
      });
    });

    // Set default date range if no visits found
    if (!minDate || !maxDate) {
      const now = new Date();
      minDate = new Date(now.getTime() - (24 * 60 * 60 * 1000)); // 24 hours ago
      maxDate = now;
    }

    // Add some padding to the date range
    const timePadding = (maxDate.getTime() - minDate.getTime()) * 0.05;
    minDate = new Date(minDate.getTime() - timePadding);
    maxDate = new Date(maxDate.getTime() + timePadding);

    const locations = Array.from(locationSet).sort();
    
    return {
      bars,
      locations,
      dateRange: [minDate, maxDate],
      maxDuration,
      totalLocations: allActiveLocations.length,
      currentPage,
      locationsPerPage
    };
  }, [dwellLocations, deviceBreakdown, deviceResults, isMultiDevice, currentPage, selectedDeviceFilter]);

  // Tooltip setup
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip,
  } = useTooltip<GanttBar>();

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    scroll: true,
  });

  // Create scales
  const timeScale = scaleTime({
    domain: ganttData.dateRange,
    range: [0, innerWidth],
  });

  const locationScale = scaleBand({
    domain: ganttData.locations,
    range: [0, innerHeight],
    padding: 0.2,
  });

  const uniqueEntityIds = Array.from(new Set(ganttData.bars.map(bar => bar.entityId)));
  const entityColorScale = scaleOrdinal({
    domain: uniqueEntityIds,
    range: ENTITY_COLORS,
  });

  // Empty state
  if (ganttData.bars.length === 0) {
    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box textAlign="center" py={4}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Timeline Data Available
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.875rem', opacity: 0.8 }}>
              {isMultiDevice 
                ? "Multi-device analysis did not produce visit records with start/end times for timeline visualization." 
                : "Single-device analysis did not contain visit records with temporal data for timeline visualization."
              }
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Dwell Location Timeline
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Timeline showing visit durations across {ganttData.locations.length} locations. 
              Data from {isMultiDevice ? 'multiple device types' : 'single device analysis'}.
            </Typography>
          </Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
            <Box display="flex" gap={1}>
              <Chip 
                label={`${ganttData.locations.length} locations`} 
                size="small" 
                color="primary" 
              />
              <Chip 
                label={`${ganttData.bars.length} visits`} 
                size="small" 
                color="secondary" 
              />
              <Chip 
                label={isMultiDevice ? "Multi-device data" : "Single-device data"} 
                size="small" 
                variant="outlined"
              />
            </Box>
            
            {/* Pagination Controls */}
            {ganttData.totalLocations && ganttData.totalLocations > (ganttData.locationsPerPage || 25) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Page {(ganttData.currentPage || 0) + 1} of {Math.ceil(ganttData.totalLocations / (ganttData.locationsPerPage || 25))}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton
                    size="small"
                    disabled={currentPage === 0}
                    onClick={() => setCurrentPage(currentPage - 1)}
                    sx={{ color: 'text.secondary' }}
                  >
                    <ChevronLeft />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={currentPage >= Math.ceil((ganttData.totalLocations || 0) / locationsPerPage) - 1}
                    onClick={() => setCurrentPage(currentPage + 1)}
                    sx={{ color: 'text.secondary' }}
                  >
                    <ChevronRight />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Stack>
        </Box>

        <Box 
          ref={containerRef} 
          sx={{ 
            overflow: 'auto',
            width: '100%',
            '& svg': { display: 'block', width: '100%' }
          }}
        >
          <svg width={chartWidth} height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
            <Group left={MARGIN.left} top={MARGIN.top}>
              {/* Y-axis (locations) */}
              <AxisLeft
                scale={locationScale}
                tickFormat={(locationName) => {
                  // Truncate long location names
                  const name = locationName.toString();
                  return name.length > 25 ? name.substring(0, 25) + '...' : name;
                }}
                tickLabelProps={{
                  dx: -8,
                  dy: 3,
                  fontSize: 11,
                  textAnchor: 'end',
                  fill: '#666',
                }}
                stroke="#e0e0e0"
                tickStroke="#e0e0e0"
              />
              
              {/* X-axis (time) */}
              <AxisBottom
                top={innerHeight}
                scale={timeScale}
                tickFormat={(date) => {
                  const d = date as Date;
                  const now = new Date();
                  const diffDays = Math.abs((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
                  
                  if (diffDays < 1) {
                    // Same day: show time
                    return d.toLocaleTimeString('en-US', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    });
                  } else {
                    // Different day: show date
                    return d.toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric' 
                    });
                  }
                }}
                tickLabelProps={{
                  dy: 4,
                  fontSize: 10,
                  textAnchor: 'middle',
                  fill: '#666',
                }}
                stroke="#e0e0e0"
                tickStroke="#e0e0e0"
              />

              {/* Gantt bars */}
              {ganttData.bars.map((bar, index) => {
                const barY = locationScale(bar.locationName) || 0;
                const barHeight = locationScale.bandwidth();
                const barX = timeScale(bar.startTime);
                const barWidth = Math.max(2, timeScale(bar.endTime) - barX);
                
                // Choose color based on device type or entity
                const fillColor = bar.deviceType && DEVICE_COLORS[bar.deviceType as keyof typeof DEVICE_COLORS]
                  ? DEVICE_COLORS[bar.deviceType as keyof typeof DEVICE_COLORS]
                  : entityColorScale(bar.entityId);

                return (
                  <rect
                    key={`bar-${index}`}
                    x={barX}
                    y={barY}
                    width={barWidth}
                    height={barHeight}
                    fill={fillColor}
                    fillOpacity={0.8}
                    stroke={fillColor}
                    strokeWidth={1}
                    rx={2}
                    onMouseEnter={(event) => {
                      const eventSvgCoords = {
                        x: event.clientX,
                        y: event.clientY,
                      };
                      showTooltip({
                        tooltipData: bar,
                        tooltipTop: eventSvgCoords.y,
                        tooltipLeft: eventSvgCoords.x,
                      });
                    }}
                    onMouseLeave={() => {
                      hideTooltip();
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                );
              })}
            </Group>
          </svg>
        </Box>

        {/* Legend */}
        <Box mt={2} display="flex" flexWrap="wrap" gap={1} justifyContent="center">
          {isMultiDevice && Object.keys(DEVICE_COLORS).map((deviceType) => (
            <Box key={deviceType} display="flex" alignItems="center" gap={0.5}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: DEVICE_COLORS[deviceType as keyof typeof DEVICE_COLORS],
                  borderRadius: 1,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {deviceType}
              </Typography>
            </Box>
          ))}
          
          {!isMultiDevice && (
            <Typography variant="caption" color="text.secondary">
              Each bar represents a visit duration. Hover for details.
            </Typography>
          )}
        </Box>

        {/* Tooltip */}
        {tooltipOpen && tooltipData && (
          <TooltipInPortal top={tooltipTop} left={tooltipLeft} style={tooltipStyles}>
            <div>
              <strong>{tooltipData.locationName}</strong>
              <br />
              <strong>Start:</strong> {tooltipData.startTime.toLocaleString()}
              <br />
              <strong>End:</strong> {tooltipData.endTime.toLocaleString()}
              <br />
              <strong>Duration:</strong> {Math.round(tooltipData.duration / 60)} minutes
              <br />
              <strong>Entity:</strong> {tooltipData.entityId}
              {tooltipData.deviceType && (
                <>
                  <br />
                  <strong>Device:</strong> {tooltipData.deviceType}
                </>
              )}
            </div>
          </TooltipInPortal>
        )}
      </CardContent>
    </Card>
  );
};

export default DwellGanttChart;
